<template>
  <div v-if="project && project.attributes">
    <v-speed-dial v-show="permit.canCreateBoard(project) || permit.canCreateTicket(project)"
      v-model="fab"
      :bottom="true"
      :right="true"
      direction="top"
      :open-on-hover="true"
      transition='slide-y-reverse-transition'
      fixed
      >
      <v-btn
        slot="activator"
        v-model="fab"
        color="#0155a8"
        dark
        fab
        fixed
        >
        <v-icon>add</v-icon>
        <v-icon>close</v-icon>
      </v-btn>

      <v-tooltip bottom color="#fff" v-if="permit.canCreateTicket(project)">
        <v-btn fab
          color="#3a9cfc"
          small
          slot="activator"
          @click="newTicket = !newTicket"
        >
          <v-icon small color="white">local_activity</v-icon>
        </v-btn>
        <span class="black--text">Create new ticket</span>
      </v-tooltip>

      <v-tooltip bottom color="#fff" v-if="permit.canCreateBoard(project)">
        <v-btn fab
          color="#0155a8"
          small
          slot="activator"
          @click="newBoard = !newBoard"
        >
          <v-icon small color="white">view_carousel</v-icon>
        </v-btn>
        <span class="black--text">Create new board</span>
      </v-tooltip>
    </v-speed-dial>

    <v-card class="mb-3 sticky-element" :class="stickyClass">
      <v-layout row wrap justify-space-between >
        <v-flex xs12 sm6 class="pa-2">
          <v-layout row wrap>
            <v-flex shrink class="text-xs-center mr-3">
              <v-avatar size=48 >
                <img :src="logoThumbUrl" alt="Avatar" v-if="logoThumbUrl">
                <v-icon color="primary" large v-else>ballot</v-icon>
              </v-avatar>
            </v-flex>
            <v-flex shrink class="grey--text" >
              <h1 class="grey--text text--darken-2">
                {{project.attributes.name}}
              </h1>
              <h3>{{project.attributes.description}}</h3>
            </v-flex>
          </v-layout>
        </v-flex>

        <v-flex shrink>
          <v-switch
            class="mr-5"
            v-model="showArchived"
            :label="showArchived && 'All boards' || 'Active boards only'"
            @change="searchBoards()"
          ></v-switch>
        </v-flex>

        <v-flex shrink class="mr-3" xs6 md4 lg3>
          <v-text-field v-model="search"
            append-icon="search"
            label="Search all boards"
            single-line
            hide-details
            inline-flex
            ref="backlogSearch"
            @keyup="searchBoards"
          ></v-text-field>
        </v-flex>
      </v-layout>
    </v-card>

    <v-dialog
      :fullscreen="$vuetify.breakpoint.xsOnly"
      v-model="newBoard"
      max-width="500px"
      persistent
      >
      <boardNew :project="project"></boardNew>
    </v-dialog>

    <v-dialog
      v-if="project && (statuses.length > 0) && (members.length > 0)"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      v-model="newTicket"
      max-width="65%"
      persistent
      >
      <ticketNew :project="project" :boards="activeBoards" :statuses="statuses" :members="members"></ticketNew>
    </v-dialog>

    <v-layout row wrap mb-4>
      <v-flex>
        <!-- backlog just doesnt have an actual board, so we need to pass the project in instead -->
        <board :project="project" :projectBoards="availableBoards" :projectStatuses="statuses" :projectMembers="members" :backlog="true"></board>
      </v-flex>
    </v-layout>

    <v-layout v-for="board in availableBoards"
      :key="board.id"
      row wrap
      mb-4
      >
      <v-flex>
        <board :project="project" :board="board" :projectBoards="availableBoards" :projectStatuses="statuses" :projectMembers="members" :backlog="false" v-show="filteredBoards.find(b => parseInt(b.id) === parseInt(board.id))"></board>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
  import { debounce } from 'debounce'
  import { EventBus } from '@/utilities/event-bus'
  import authentication from '@/utilities/authentication'
  import permissions from '@/utilities/permissions'

  import projectService from '@/services/project'
  import projectBoard from '@/components/project/_board'

  import ticketNew from '@/components/ticket/_new'

  import boardService from '@/services/board'
  import boardNew from '@/components/board/_new'

  import statusService from '@/services/status'
  import memberService from '@/services/member'

  export default {
    name: 'project-backlog',

    components: {
      board: projectBoard,
      ticketNew: ticketNew,
      boardNew: boardNew
    },

    props: ['id'],

    data () {
      return {
        auth: authentication,
        permit: permissions,
        newBoard: false,
        newTicket: false,
        project: null,
        boards: [],
        statuses: [],
        members: [],
        fab: false,
        search: '',
        filteredBoards: [],
        showArchived: false
      }
    },

    mounted () {
      EventBus.$on('ticket-created', (payload) => { this.newTicket = false })
      EventBus.$on('board-created', (payload) => {
        this.newBoard = false
        if (payload) {
          this.boards.push(payload)
        }
      })

      EventBus.$on('board-deleted', (payload) => {
        if (payload) {
          this.boards = this.boards.filter(brd => parseInt(brd.id) !== parseInt(payload.id))
        }
      })

      EventBus.$on('board-search-match', (payload) => {
        // filteredBoards should at least also include the board we just received an ID for
        let hasBoard = this.filteredBoards.find(b => parseInt(b.id) === parseInt(payload))
        if (!hasBoard) {
          let availableBoard = this.availableBoards.find(b => parseInt(b.id) == parseInt(payload))
          if (availableBoard) {
            this.filteredBoards.push(availableBoard)
          }
        }
      })

      this.loadProject()
    },

    computed: {
      logoThumbUrl () {
        if (this.project.links && this.project.links.logo_thumb_uri) {
          return (process.env.API_URL + this.project.links.logo_thumb_uri)
        }
        return null
      },

      stickyClass () {
        if (window.innerWidth > 599) {
          if (this.scroll > 0) {
            return 'elevation-8'
          } else {
            return ''
          }
        }
      },

      availableBoards () {
        if (this.showArchived) {
          return this.boards
        } else {
          return this.activeBoards
        }
      },

      activeBoards () {
        return this.boards.filter(brd => !brd.attributes.archived)
      }
    },

    methods: {
      loadProject () {
        projectService.show(
          (data) => {
            this.project = Object.assign({}, data)
            document.title = `${this.project.attributes.name} - Backlog`
            EventBus.$emit('project-context', data.id)
            this.loadBoards()
            this.loadStatuses()
            this.loadMembers()
          },
          (err) => {
            console.log(err)
          },
          this.id
        )
      },

      loadBoards () {
        boardService.projectIndex(
          (data) => {
            let sortedBoards
            if (this.auth.preferences().board_sort_by === 'due') {
              sortedBoards = data.sort((a, b) => {
                let aStr = (a.attributes.due_on || '').toLowerCase()
                let bStr = (b.attributes.due_on || '').toLowerCase()
                // if the dates are the same, fall back to the name
                if (aStr === bStr) {
                  aStr = a.attributes.name.toLowerCase()
                  bStr = b.attributes.name.toLowerCase()
                }
                return aStr.localeCompare(bStr)
              })
            } else {
              sortedBoards = data.sort((a, b) => { return a.attributes.name.toLowerCase().localeCompare(b.attributes.name.toLowerCase()) })
            }
            // asc or desc
            if (this.auth.preferences().board_sort_order === 'desc') {
              sortedBoards.reverse()
            }

            this.boards = sortedBoards
            this.filteredBoards = this.activeBoards
          },
          (err) => {
            console.log(err)
          },
          this.project.id
        )
      },

      loadStatuses () {
        statusService.projectIndex(
          (data) => {
            this.statuses = data.sort((a, b) => { return parseInt(a.position) - parseInt(b.position) })
          },
          (err) => {
            console.log(err)
          },
          this.project.id
        )
      },

      loadMembers () {
        memberService.projectIndex(
          (data) => {
            this.members = data.filter(m => !m.attributes.discarded_at).sort((a, b) => { return a.attributes.name.toLowerCase().localeCompare(b.attributes.name.toLowerCase()) })
          },
          (err) => {
            console.log(err)
          },
          this.project.id
        )
      },

      searchBoards: debounce(function () {
        EventBus.$emit('board-search', this.search)
        // We need to probably extend this to search for more things, for now a very basic text search will sufffice
        this.$nextTick(() => {
          if (this.search.length > 0) {
            let regex = new RegExp(this.search, 'ig')
            this.filteredBoards = this.availableBoards.filter(b => b.attributes.name.match(regex))
            // This has to be done after the boards are first filtered
          } else {
            this.filteredBoards = this.availableBoards
          }
        })
      }, 1000)
    }
  }
</script>

<style lang="scss" scoped>
  /* for some reason vuetify speed dials dont position the same way as naken fabs, so we have to force the issue*/
  .v-speed-dial {
    bottom: 96px;
    right: 72px;

    .v-speed-dial__list .v-tooltip {
      display: block;
      margin: 0 auto;
    }
  }

  .project-search {
    border-bottom: 1px solid lightgrey;
  }

  @media screen and (min-width: 600px) {
    .sticky-element{
      position: sticky;
      position: -webkit-sticky;
      top: 65px;
      z-index: 5;
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/project/backlog.vue